import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { exerciseBankService, type ExerciseSet } from "@/services/exerciseBankService";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  BookOpen, 
  Sparkles,
  ArrowRight,
  Search,
  Filter,
  CheckCircle,
  Clock
} from "lucide-react";

export default function BanqueExercicesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [exerciseSets, setExerciseSets] = useState<ExerciseSet[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");
  const [selectedLevel, setSelectedLevel] = useState<string>("ALL");

  useEffect(() => {
    const loadSets = () => {
      const sets = exerciseBankService.getExerciseSets();
      setExerciseSets(sets);
    };

    loadSets();

    const handleUpdate = () => loadSets();
    window.addEventListener("exerciseSetsUpdated", handleUpdate);
    return () => window.removeEventListener("exerciseSetsUpdated", handleUpdate);
  }, []);

  // Filter sets based on input
  const filteredSets = exerciseSets.filter(set => {
    const matchesSearch = set.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "ALL" || set.category === selectedCategory;
    const matchesLevel = selectedLevel === "ALL" || set.level === selectedLevel;
    return matchesSearch && matchesCategory && matchesLevel;
  });

  return (
    <div className="max-w-5xl mx-auto space-y-10 pb-16 animate-in fade-in duration-300 text-left">
      
      {/* Page Header */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge className="bg-primary/5 hover:bg-primary/5 text-primary border-none font-bold text-[10px] tracking-wider uppercase px-2.5 py-0.5 rounded-full shadow-3xs">
            Module Pratique
          </Badge>
          <Badge variant="outline" className="text-slate-500 font-bold text-[10px] tracking-wider uppercase border-slate-200 px-2.5 py-0.5 rounded-full shadow-3xs">
            TEF Canada
          </Badge>
        </div>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-none">
              Banque d'Exercices
            </h1>
            <p className="text-slate-500 text-xs md:text-sm font-semibold max-w-2xl leading-relaxed">
              Pratiquez les épreuves de Compréhension Écrite (CE) et Compréhension Orale (CO) avec des questions types conformes à la grille NCLC.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Quick stats */}
            <div className="px-3.5 py-1.5 rounded-xl border border-slate-100 bg-white shadow-3xs flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
              <Clock className="w-3.5 h-3.5 text-indigo-500" />
              <span>{exerciseSets.length} sets disponíveis</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between border-y border-slate-100 py-5 bg-slate-50/20 px-4 rounded-2xl border">
        {/* Search Input */}
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un exercice..."
            className="w-full pl-10 pr-4 py-2 text-xs md:text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary font-semibold text-slate-800 placeholder-slate-400"
          />
        </div>

        {/* Category & Level Dropdowns */}
        <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-1.5 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary font-bold text-slate-700"
            >
              <option value="ALL">Toutes les épreuves</option>
              <option value="CE">Compréhension Écrite (CE)</option>
              <option value="CO">Compréhension Orale (CO)</option>
            </select>
          </div>

          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            className="px-3 py-1.5 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary font-bold text-slate-700"
          >
            <option value="ALL">Tous les niveaux</option>
            <option value="A1">A1</option>
            <option value="A2">A2</option>
            <option value="B1">B1</option>
            <option value="B2">B2</option>
            <option value="C1">C1</option>
            <option value="C2">C2</option>
          </select>
        </div>
      </div>

      {/* Grid of cards */}
      {filteredSets.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-slate-200 rounded-3xl bg-white">
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3 stroke-[1.25]" />
          <h4 className="font-extrabold text-slate-800 text-sm md:text-base">Aucun exercice trouvé</h4>
          <p className="text-slate-400 text-xs mt-1.5 font-semibold max-w-sm mx-auto">
            Ajustez vos filtres de recherche ou importez de nouveaux exercices dans le panneau d'administration.
          </p>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filteredSets.map((set) => (
            <Card 
              key={set.id} 
              className="border border-slate-100 hover:border-slate-200/80 bg-white rounded-2xl overflow-hidden shadow-xs hover:shadow-sm transition-all duration-300 flex flex-col justify-between"
            >
              <CardHeader className="p-6 pb-4 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <Badge className="bg-slate-100 hover:bg-slate-100 text-slate-600 border-none font-bold text-[8px] tracking-wide uppercase px-2.5 py-0.5 rounded shadow-3xs">
                    {set.category === "CE" ? "Compréhension Écrite" : set.category === "CO" ? "Compréhension Orale" : set.category}
                  </Badge>
                  <Badge className="bg-primary/5 hover:bg-primary/5 text-primary border-none font-bold text-[8px] tracking-wide uppercase px-2.5 py-0.5 rounded shadow-3xs">
                    Niveau {set.level}
                  </Badge>
                </div>

                <CardTitle className="text-sm md:text-base font-extrabold text-slate-900 leading-tight">
                  {set.title}
                </CardTitle>
              </CardHeader>

              <CardContent className="p-6 pt-0 space-y-5">
                <div className="flex items-center justify-between text-[11px] font-semibold text-slate-400 border-t border-slate-50 pt-4 mt-auto">
                  <span className="flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-amber-500 fill-amber-500/10" />
                    {set.questions.length} questions
                  </span>
                  <span className="text-[10px] text-slate-300">
                    NCLC {set.level}
                  </span>
                </div>

                <Button
                  onClick={() => navigate(`/exercises/${set.id}`)}
                  variant="secondary"
                  className="w-full h-10 border border-slate-200 hover:border-slate-300 hover:bg-slate-50/50 text-slate-700 font-bold rounded-xl text-xs px-5 shadow-3xs transition-all flex items-center justify-center gap-1"
                >
                  Commencer
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 transition-colors" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

    </div>
  );
}
